import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  ArrowLeft, Plus, Minus, ShoppingCart, Trash2, FileText, 
  CheckCircle, Lightbulb, Camera, Speaker, Tv, Armchair, 
  Sparkles, Users, Tag
} from 'lucide-react';

const categoryIcons = {
  'Stage & Setup': Lightbulb,
  'Lighting': Lightbulb,
  'Sound & Audio': Speaker,
  'Video & Display': Tv,
  'Camera & Recording': Camera,
  'Furniture & Decor': Armchair,
  'Branding & Signage': Tag,
  'Special Effects': Sparkles,
  'Technical Crew': Users,
};

export default function ElementSheetPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [elements, setElements] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mySheets, setMySheets] = useState([]);
  const [showSheetsDialog, setShowSheetsDialog] = useState(false);

  // Form state
  const [sheetName, setSheetName] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventDateObj, setEventDateObj] = useState(null);
  const [venue, setVenue] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, elementsRes] = await Promise.all([
        api.getElementCategories(),
        api.getElementsCatalog()
      ]);
      setCategories(categoriesRes.data);
      setElements(elementsRes.data);
    } catch (error) {
      console.error('Failed to fetch elements:', error);
    }
  };

  const fetchMySheets = async () => {
    try {
      const res = await api.getElementSheets();
      setMySheets(res.data);
      setShowSheetsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch element sheets');
    }
  };

  const addToCart = (element) => {
    const existing = cart.find(item => item.element_id === element.id);
    if (existing) {
      setCart(cart.map(item => 
        item.element_id === element.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        element_id: element.id,
        element: element,
        quantity: 1,
        days: 1,
        notes: ''
      }]);
    }
    toast.success(`Added ${element.name}`);
  };

  const updateCartItem = (elementId, field, value) => {
    setCart(cart.map(item => 
      item.element_id === elementId 
        ? { ...item, [field]: value }
        : item
    ));
  };

  const removeFromCart = (elementId) => {
    setCart(cart.filter(item => item.element_id !== elementId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      return sum + (item.element.base_price * item.quantity * item.days);
    }, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.18;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async () => {
    if (!sheetName || !eventName || !eventDateObj || !venue) {
      toast.error('Please fill in all event details');
      return;
    }

    if (cart.length === 0) {
      toast.error('Please add at least one element');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createElementSheet({
        name: sheetName,
        event_name: eventName,
        event_date: eventDateObj.toISOString().split('T')[0],
        venue: venue,
        items: cart.map(item => ({
          element_id: item.element_id,
          quantity: item.quantity,
          days: item.days,
          notes: item.notes
        }))
      });

      // Confetti celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#d4af37', '#1e3a5f', '#ffffff']
      });

      toast.success('Element Sheet created successfully!');
      setShowCheckoutDialog(false);
      setCart([]);
      setSheetName('');
      setEventName('');
      setEventDate('');
      setVenue('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create element sheet');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredElements = selectedCategory === 'all' 
    ? elements 
    : elements.filter(e => e.category === selectedCategory);

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Please Login</h2>
          <p className="text-slate-800 mb-4">You need to be logged in to create element sheets</p>
          <Link to="/"><Button>Go to Login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-navy border-b border-navy-800 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/client">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <h1 className="font-heading text-xl font-semibold text-white">Event Element Sheet</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={fetchMySheets} className="text-white hover:bg-white/10">
              <FileText className="w-4 h-4 mr-2" /> My Sheets
            </Button>
            <Button 
              onClick={() => setShowCheckoutDialog(true)} 
              className="bg-gold text-navy hover:bg-gold-light relative"
              disabled={cart.length === 0}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              View Quote
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Category Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-navy' : ''}
            >
              All Elements
            </Button>
            {categories.map(category => {
              const Icon = categoryIcons[category] || Tag;
              return (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? 'bg-navy' : ''}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {category}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Elements Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredElements.map(element => {
            const cartItem = cart.find(item => item.element_id === element.id);
            return (
              <div 
                key={element.id} 
                className={`bg-white rounded-lg border p-4 transition-all ${
                  cartItem ? 'border-gold ring-1 ring-gold' : 'hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-xs text-slate-700 uppercase tracking-wide">{element.category}</span>
                    <h3 className="font-semibold text-navy">{element.name}</h3>
                  </div>
                  {cartItem && (
                    <span className="bg-gold text-navy text-xs px-2 py-1 rounded-full font-medium">
                      {cartItem.quantity} in cart
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-800 mb-3">{element.description}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-lg font-bold text-navy">₹{element.base_price.toLocaleString()}</span>
                    <span className="text-sm text-slate-800">/{element.unit}</span>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => addToCart(element)}
                    className={cartItem ? 'bg-gold text-navy' : 'bg-navy'}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Floating Cart Summary */}
        {cart.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-navy text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-6 z-50">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-gold" />
              <span className="font-medium">{cartItemCount} items</span>
            </div>
            <div className="h-6 w-px bg-white/20"></div>
            <div>
              <span className="text-gold font-bold">₹{calculateTotal().toLocaleString()}</span>
              <span className="text-slate-300 text-sm ml-1">(incl. GST)</span>
            </div>
            <Button 
              onClick={() => setShowCheckoutDialog(true)}
              className="bg-gold text-navy hover:bg-gold-light"
            >
              Get Quote
            </Button>
          </div>
        )}
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">Create Element Sheet</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Event Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sheet Name</Label>
                <Input 
                  value={sheetName} 
                  onChange={(e) => setSheetName(e.target.value)}
                  placeholder="Q1 Annual Meet Equipment"
                />
              </div>
              <div>
                <Label>Event Name</Label>
                <Input 
                  value={eventName} 
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="Annual Sales Conference 2025"
                />
              </div>
              <div>
                <Label>Event Date</Label>
                <DatePicker
                  selected={eventDateObj}
                  onChange={(date) => setEventDateObj(date)}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                />
              </div>
              <div>
                <Label>Venue</Label>
                <Input 
                  value={venue} 
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="Taj Palace, Delhi"
                />
              </div>
            </div>

            {/* Cart Items */}
            <div>
              <h3 className="font-semibold text-navy mb-3">Selected Elements</h3>
              {cart.length === 0 ? (
                <p className="text-slate-700 text-center py-8">No elements selected yet</p>
              ) : (
                <div className="space-y-3">
                  {cart.map(item => (
                    <div key={item.element_id} className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-navy">{item.element.name}</h4>
                          <p className="text-sm text-slate-700">{item.element.category}</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeFromCart(item.element_id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs">Quantity</Label>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => updateCartItem(item.element_id, 'quantity', Math.max(1, item.quantity - 1))}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-12 text-center font-medium">{item.quantity}</span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => updateCartItem(item.element_id, 'quantity', item.quantity + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Days</Label>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => updateCartItem(item.element_id, 'days', Math.max(1, item.days - 1))}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-12 text-center font-medium">{item.days}</span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => updateCartItem(item.element_id, 'days', item.days + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-right">
                          <Label className="text-xs">Line Total</Label>
                          <p className="text-lg font-bold text-navy">
                            ₹{(item.element.base_price * item.quantity * item.days).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totals */}
            {cart.length > 0 && (
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-slate-800">
                  <span>Subtotal</span>
                  <span>₹{calculateSubtotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-slate-800">
                  <span>GST (18%)</span>
                  <span>₹{calculateTax().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-navy pt-2 border-t">
                  <span>Total Amount</span>
                  <span className="text-gold">₹{calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <Button 
                variant="outline" 
                onClick={() => setShowCheckoutDialog(false)}
                className="flex-1"
              >
                Continue Shopping
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting || cart.length === 0}
                className="flex-1 bg-gold text-navy hover:bg-gold-light"
              >
                {isSubmitting ? 'Creating...' : 'Create Element Sheet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Sheets Dialog */}
      <Dialog open={showSheetsDialog} onOpenChange={setShowSheetsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-navy">My Element Sheets</DialogTitle>
          </DialogHeader>

          {mySheets.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto mb-4 text-navy-300" />
              <p className="text-slate-700">No element sheets created yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {mySheets.map(sheet => (
                <div key={sheet.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-navy">{sheet.name}</h3>
                      <p className="text-sm text-slate-700">{sheet.event_name} • {sheet.venue}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      sheet.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' :
                      sheet.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      sheet.status === 'submitted' ? 'bg-amber-100 text-amber-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {sheet.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{sheet.items?.length || 0} items • {new Date(sheet.event_date).toLocaleDateString()}</span>
                    <span className="font-bold text-gold">₹{sheet.total_amount?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
